import { useState } from "react";
import { ApplicantInfo } from "../../../components/Applicant/ApplicantInfo";
import { RadioButtonGroup } from "../../../components/Decision/RadioButtonGroup";
import ErrorMessages from "../../../components/ErrorMessages/ErrorMessage";
import { FormNumber } from "../../../components/Input/FormNumber";
import { FormText } from "../../../components/Input/FormText";
import { FormTextArea } from "../../../components/Input/FormTextArea";
import { CommonUserTaskNew } from "../CommonUserTaskNew";
import type { ComponentConfig } from "../types";
import { GapContainer } from "../update-application/style";
import type {
  FormData,
  FormDataErrors,
  FormDataKeys,
  FormOptions
} from "../update-application/types";
import { validateFormData } from "../update-application/validation";

const formOptions: FormOptions[] = [
  { label: "Total loan amount (incl. refinance)", value: "loanAmount" },
  { label: "External refinance amount", value: "refinanceAmount" },
  { label: "Startup fee", value: "startupFee" },
  { label: "Loan duration", value: "loanDuration" }
];

const HandleRejection = (props: any) => {
  type RejectionDecision = "retry-rejection" | "approve-rejection" | null;
  const decisionErrorMessage = "Please select a decision.";
  const [errorMessages, setErrorMessages] = useState<string>("");

  const applicationData = props.flow?.data?.agentApiApplication;
  const updatedValues = props.flow?.data?.updates?.updatedValues;

  const applicantInfo = applicationData?.applicant;
  const coApplicantInfo = applicationData?.coapplicant
    ? { ...applicationData.coapplicant, isCoApplicant: true }
    : null;

  const {
    loanAmount: updatedLoanAmount,
    refinanceAmount: updatedRefinanceAmount,
    startupFee: updatedStartupFee,
    loanTerms: updatedLoanDuration,
    loanInterestRate: updatedInterestRate
  } = updatedValues || {};

  const loadedAnswer = props.task?.data?.updateApplication ?? {
    loanAmount: updatedLoanAmount ?? "",
    refinanceAmount: updatedRefinanceAmount ?? "",
    startupFee: updatedStartupFee ?? "",
    loanDuration: updatedLoanDuration ?? "",
    interestRate: updatedInterestRate ?? "",
    comment: ""
  };
  const loadedDecision: RejectionDecision = props.task?.data?.decision ?? null;

  const [formData, setFormData] = useState<FormData>(loadedAnswer);
  const [decision, setDecision] = useState<RejectionDecision>(loadedDecision);
  const [errors, setErrors] = useState<FormDataErrors>({});

  const toNumberOrUndefined = (value: unknown): number | undefined => {
    if (typeof value === "number") {
      return value;
    }
    if (typeof value === "string" && value.trim().length > 0) {
      return Number(value);
    }
    return undefined;
  };

  async function handleComplete(): Promise<void> {
    if (decision === null) {
      setErrorMessages(decisionErrorMessage);
      return;
    }

    const validationErrors = validateFormData(formData);

    if (
      Object.values(validationErrors).some((error) => error.hasError === true)
    ) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    const validatedData = {
      loanAmount: toNumberOrUndefined(formData.loanAmount),
      refinanceAmount: toNumberOrUndefined(formData.refinanceAmount),
      startupFee: toNumberOrUndefined(formData.startupFee),
      loanTerms: toNumberOrUndefined(formData.loanDuration),
      loanInterestRate: toNumberOrUndefined(formData.interestRate),
      comment: formData.comment,
      decision
    };

    props.complete(
      validatedData,
      () => {},
      (e: any) => {
        setErrorMessages(e?.detail);
      }
    );
  }

  function handleSave(): void {
    props.save(
      {
        updateApplication: formData,
        decision
      },
      () => {},
      () => {}
    );
  }

  function handleFormUpdate(
    name: string,
    value: FormData[FormDataKeys] | string
  ): void {
    setFormData((prevState) => ({ ...prevState, [name]: value }));
  }

  const formConfig: ComponentConfig[] = [
    {
      component: (
        <GapContainer>
          <ApplicantInfo applicant={applicantInfo} />
          {coApplicantInfo && <ApplicantInfo applicant={coApplicantInfo} />}
        </GapContainer>
      ),
      order: 1
    },
    {
      component: (
        <GapContainer>
          {formOptions.map(
            ({ label, value }: { label: string; value: FormDataKeys }) => (
              <FormNumber
                key={value}
                label={label}
                name={value}
                placeholder={`Enter ${label.toLowerCase()}`}
                value={formData[value]}
                onChange={(number) => handleFormUpdate(value, number)}
                hasError={!!errors[value]?.hasError}
                errorMessage={errors?.[value]?.message}
                required={false}
              />
            )
          )}
          <FormText
            label="Interest rate"
            name="interest rate"
            onChange={(value: string) =>
              handleFormUpdate("interestRate", value)
            }
            defaultValue={formData.interestRate}
            hasError={!!errors.interestRate?.hasError}
            errorMessage={errors?.interestRate?.message}
            placeholder="Enter interest rate"
            required={false}
          />
        </GapContainer>
      ),
      order: 2
    },
    {
      component: (
        <RadioButtonGroup
          radioButtonValue={decision}
          onChange={(value: RejectionDecision) => setDecision(value)}
          title="Decision"
          options={[
            { value: "retry-rejection", label: "Retry credit evaluation" },
            { value: "approve-rejection", label: "Complete as rejected" }
          ]}
          id="handling-rejection-decision"
        />
      ),
      order: 3
    },
    {
      component: (
        <FormTextArea
          label="Internal comment"
          name="comment"
          placeholder={"Enter your comment here"}
          onChange={(value: string) => handleFormUpdate("comment", value)}
          defaultValue={formData.comment}
        />
      ),
      order: 4
    },
    {
      component: <ErrorMessages errors={[errorMessages]} />,
      order: 5,
      hide: errorMessages === ""
    }
  ];

  return (
    <CommonUserTaskNew
      handleComplete={handleComplete}
      handleSave={handleSave}
      formConfig={formConfig}
      overrideMasonryColumns={1}
      disablePrimary={decision === null}
      labelPrimary="Apply decision"
      formWidth={400}
    />
  );
};

export default HandleRejection;
